import { Phone, MessageCircle, FileText, MapPin, Clock, Navigation, Building2 } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { usePublicConfig } from "@/hooks/usePublicConfig";
import { trackEvent, getWhatsAppUrl, getPhoneHref, isOpenNow } from "@/lib/tracking";
import { buildDirectionsUrl, trackDirectionsClick, REGISTERED } from "@/lib/location-links";

interface Props {
  /** Area name e.g. "Poplar" — used in WhatsApp prefill and headline. */
  areaName: string;
  /** Optional postcode/area context (e.g. "E14"). */
  postcode?: string;
  /** Tracking source identifier — appended to `location` for analytics. */
  source?: string;
}

/**
 * Per-area Call / WhatsApp / Book MOT CTA block.
 * Pulls verified address + opening hours from the admin compliance block
 * (single source of truth) and uses the routing-aware tracking helpers
 * so phone numbers and WhatsApp messages always reflect admin overrides.
 */
export function AreaContactCTA({ areaName, postcode, source = "area_cta" }: Props) {
  const { compliance } = usePublicConfig();
  const [status, setStatus] = useState<{ open: boolean; message: string } | null>(null);
  useEffect(() => {
    setStatus(isOpenNow());
    const id = window.setInterval(() => setStatus(isOpenNow()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  const phoneHref = getPhoneHref();
  const phoneNumber = phoneHref.replace(/^tel:/, "");
  const whatsappUrl = getWhatsAppUrl(undefined, {
    name: undefined,
  });
  // Prefilled WhatsApp greeting tailored to the area.
  const areaWhatsApp = `https://wa.me/${whatsappUrl.match(/wa\.me\/(\d+)/)?.[1] || "442075372447"}?text=${encodeURIComponent(
    `Hi, I'm in ${areaName}${postcode ? ` (${postcode})` : ""} and I'd like to book my MOT or service.`
  )}`;

  const areaSlug = `area_${areaName.toLowerCase().replace(/\s+/g, "_")}`;
  const directionsHref = buildDirectionsUrl("entrance");
  const registeredHref = buildDirectionsUrl("registered");

  const todayLine = compliance.openingHours
    .map((slot) => `${slot.days?.[0]?.slice(0, 3) || "Mon"}–${slot.days?.[slot.days.length - 1]?.slice(0, 3) || "Sat"} ${slot.opens || "09:00"}–${slot.closes || "18:00"}`)
    .join(" · ");

  return (
    <section className="py-12 md:py-16">
      <div className="mx-auto max-w-5xl px-4 md:px-6">
        <div className="rounded-3xl border border-border bg-gradient-to-br from-card via-card to-secondary/40 p-6 md:p-10 shadow-xl shadow-primary/[0.04]">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8 items-center">
            {/* Left — message + verified info */}
            <div>
              <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-primary">Drivers in {areaName}</p>
              <h2 className="mt-2 text-3xl md:text-4xl font-extrabold tracking-tight text-foreground leading-tight">
                Call, WhatsApp or book your MOT — we'll have you on the road today.
              </h2>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                You're {areaName === compliance.addressLocality ? "minutes" : "a short drive"} from the workshop.
                Choose how you'd like to get in touch — same team, same prices, every channel.
              </p>

              {/* Verified address + status pills */}
              <div className="mt-5 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary border border-border px-3 py-1.5 text-xs font-semibold text-foreground">
                  <MapPin className="h-3.5 w-3.5 text-primary" />
                  {compliance.streetAddress}, {compliance.postalCode}
                </span>
                {status && (
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${
                      status.open
                        ? "bg-[oklch(0.96_0.06_150)] text-[oklch(0.35_0.12_150)] border border-[oklch(0.85_0.1_150)]"
                        : "bg-secondary text-muted-foreground border border-border"
                    }`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${status.open ? "bg-[oklch(0.55_0.18_150)] animate-pulse" : "bg-muted-foreground"}`} />
                    {status.message}
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary border border-border px-3 py-1.5 text-xs font-semibold text-foreground">
                  <Clock className="h-3.5 w-3.5 text-primary" />
                  {todayLine}
                </span>
              </div>
            </div>

            {/* Right — CTA stack */}
            <div className="space-y-2.5">
              <a
                href={phoneHref}
                onClick={() => trackEvent("call_click", { location: source, source: `area_${areaName.toLowerCase().replace(/\s+/g, "_")}` })}
                className="flex items-center justify-between gap-3 rounded-2xl bg-primary px-5 py-4 text-primary-foreground shadow-lg shadow-primary/25 hover:bg-primary/90 transition-all"
              >
                <span className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-foreground/15">
                    <Phone className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-[11px] font-bold uppercase tracking-wider opacity-80">Call the workshop</span>
                    <span className="block text-base font-extrabold tracking-tight">{phoneNumber.replace("+44", "0")}</span>
                  </span>
                </span>
                <span className="text-xs font-bold opacity-80">Tap to call →</span>
              </a>

              <a
                href={areaWhatsApp}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("whatsapp_click", { location: source, source: `area_${areaName.toLowerCase().replace(/\s+/g, "_")}` })}
                className="flex items-center justify-between gap-3 rounded-2xl border border-[#25D366]/30 bg-[#25D366]/5 px-5 py-4 text-[#1ea952] hover:bg-[#25D366]/10 transition-all"
              >
                <span className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#25D366]/15">
                    <MessageCircle className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-[11px] font-bold uppercase tracking-wider">WhatsApp the team</span>
                    <span className="block text-sm font-extrabold tracking-tight text-foreground">Send a photo or your reg</span>
                  </span>
                </span>
                <span className="text-xs font-bold">Chat now →</span>
              </a>

              <Link
                to="/book"
                onClick={() => trackEvent("service_cta_click", { location: source, source: `area_${areaName.toLowerCase().replace(/\s+/g, "_")}`, action: "book_mot" })}
                className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-background px-5 py-4 text-foreground hover:border-primary/40 hover:bg-secondary/40 transition-all"
              >
                <span className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <FileText className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Online</span>
                    <span className="block text-sm font-extrabold tracking-tight">Book your MOT</span>
                  </span>
                </span>
                <span className="text-xs font-bold text-primary">Reserve a slot →</span>
              </Link>

              <div className="grid grid-cols-2 gap-2">
                <a
                  href={directionsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackDirectionsClick({ source, kind: "entrance", action: areaSlug })}
                  className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-border bg-transparent px-3 py-2.5 text-[11px] font-bold text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
                >
                  <Navigation className="h-3.5 w-3.5" />
                  Yard ({compliance.postalCode})
                </a>
                <a
                  href={registeredHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackDirectionsClick({ source, kind: "registered", action: areaSlug })}
                  className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-border bg-transparent px-3 py-2.5 text-[11px] font-bold text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
                >
                  <Building2 className="h-3.5 w-3.5" />
                  Registered ({REGISTERED.postcode})
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
